import React, { useMemo, useState } from 'react';
import {
  Star,
  Copy,
  ExternalLink,
  CheckCircle,
  MessageSquare,
  Utensils,
  Heart,
  ChevronLeft,
  Send,
  ThumbsUp,
  Sparkles,
} from 'lucide-react';
import { saveResponse } from './firebase';
import ThreeBackground from './three-bg';

const RATING_FEEDBACK: Record<number, { emoji: string; label: string; tone: string }> = {
  1: { emoji: '😔', label: '申し訳ございません…', tone: 'text-rose-600' },
  2: { emoji: '😐', label: '至らない点があったようですね', tone: 'text-amber-600' },
  3: { emoji: '🙂', label: 'ありがとうございます', tone: 'text-amber-700' },
  4: { emoji: '😊', label: '嬉しいです！', tone: 'text-orange-600' },
  5: { emoji: '🤩', label: '最高評価ありがとうございます！', tone: 'text-orange-600' },
};

const POSITIVE_TAGS = ['料理が美味しかった', '接客が丁寧', '雰囲気が良い', 'コスパ最高', '盛り付けが素敵'];
const NEGATIVE_TAGS = ['待ち時間が長い', '味が期待と違った', '接客が気になった', '店内が騒がしかった', '価格が高め'];

export default function App() {
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // 【重要】実際の運用時には、ここをご自身の店舗のGoogleマップクチコミ投稿用URLに変更してください。
  // Place IDを使ったURLが推奨されます: https://search.google.com/local/writereview?placeid=【あなたの店舗のPlace ID】
  const GOOGLE_MAP_URL = 'https://search.google.com/local/writereview?placeid=ChIJN1t_tDeuEmsRUsoyG83frY4';

  const handleRatingClick = (selectedRating: number) => {
    setRating(selectedRating);
    // タップして星の色が変わるのを見せるために少し遅延させて次のステップへ
    setTimeout(() => {
      setStep(2);
      window.scrollTo(0, 0);
    }, 600);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await saveResponse({ rating, comment });
      setStep(3);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('送信に失敗しました', err);
      alert('送信に失敗しました。通信環境をご確認のうえ、再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    if (!comment) return;

    // iframe内でも動作しやすいフォールバックを用いたコピー処理
    const textArea = document.createElement('textarea');
    textArea.value = comment;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000); // 3秒後に「コピーしました」表示を戻す
    } catch (err) {
      console.error('コピーに失敗しました', err);
      alert('コピーに失敗しました。手動でコピーしてください。');
    }
    document.body.removeChild(textArea);
  };

  const addTag = (tag: string) => {
    setComment((prev) => {
      if (!prev.trim()) return tag;
      if (prev.includes(tag)) return prev;
      return prev.endsWith('、') || prev.endsWith(' ') ? `${prev}${tag}` : `${prev}、${tag}`;
    });
  };

  const isHighRating = rating >= 4;
  const displayedRating = hoverRating || rating;
  const feedback = displayedRating > 0 ? RATING_FEEDBACK[displayedRating] : null;
  const progress = (step / 3) * 100;
  const tags = isHighRating ? POSITIVE_TAGS : NEGATIVE_TAGS;

  // 3D背景の「激しさ」を rating/step に応じて変化 (無駄に)
  // 指数的に効くので数値以上に派手になる
  const mood = step === 3 && isHighRating ? 4.5 : step === 2 ? 1.8 : 1;

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4 font-sans text-gray-800">
      {/* 背景: グラデ + うっすら放射状グロー */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50" aria-hidden />
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 10%, rgba(251,146,60,0.2), transparent 40%), radial-gradient(circle at 80% 90%, rgba(244,114,182,0.15), transparent 40%)',
        }}
        aria-hidden
      />

      {/* 無駄に3D: Three.js で回る立体たち */}
      <ThreeBackground mood={mood} />

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden relative z-10">
        {/* ヘッダー */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 pt-6 pb-4 text-center text-white">
          <div className="mx-auto bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-3 animate-bounce-slow">
            <Utensils size={32} />
          </div>
          <h1 className="text-xl font-bold">〇〇ダイニング</h1>
          <p className="text-orange-100 text-sm mt-1">アンケートにご協力ください</p>
        </div>

        {/* 進捗バー */}
        <div className="bg-orange-600 text-white">
          <div className="h-1 bg-white/20">
            <div
              className="h-full bg-white transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between px-4 py-1.5 text-[10px] text-orange-100 tabular-nums">
            <span className={step >= 1 ? 'font-bold' : 'opacity-60'}>① 評価</span>
            <span className={step >= 2 ? 'font-bold' : 'opacity-60'}>② ご感想</span>
            <span className={step >= 3 ? 'font-bold' : 'opacity-60'}>③ 完了</span>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* STEP 1: 星評価 */}
          {step === 1 && (
            <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
              <h2 className="text-xl font-bold mb-2">
                本日はご来店
                <br />
                ありがとうございました！
              </h2>
              <p className="text-gray-600 mb-6 text-sm">お店の満足度を星でお知らせください</p>

              <div className="flex justify-center gap-1 sm:gap-2 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => handleRatingClick(star)}
                    className="p-1 sm:p-2 transition-transform hover:scale-125 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-orange-300 rounded-full"
                    aria-label={`星${star}つ`}
                  >
                    <Star
                      size={48}
                      strokeWidth={1.5}
                      className={`${
                        star <= displayedRating
                          ? 'fill-orange-400 text-orange-400 drop-shadow-md'
                          : 'text-gray-200 fill-gray-100'
                      } transition-all duration-200`}
                    />
                  </button>
                ))}
              </div>

              {/* 絵文字リアクション */}
              <div className="h-16 flex flex-col items-center justify-center mb-2">
                {feedback ? (
                  <div
                    key={displayedRating}
                    className="animate-in zoom-in-50 fade-in duration-200 flex flex-col items-center gap-1"
                  >
                    <span className="text-4xl leading-none">{feedback.emoji}</span>
                    <span className={`text-xs font-bold ${feedback.tone}`}>{feedback.label}</span>
                  </div>
                ) : (
                  <div className="text-xs text-gray-300">星を選んでください</div>
                )}
              </div>

              <div className="flex justify-between text-xs text-gray-400 px-4">
                <span>不満</span>
                <span>大満足</span>
              </div>
            </div>
          )}

          {/* STEP 2: コメント入力 */}
          {step === 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <button
                onClick={() => setStep(1)}
                className="flex items-center text-gray-400 hover:text-orange-600 mb-4 transition-colors text-sm"
              >
                <ChevronLeft size={16} />
                <span>星の評価をやり直す</span>
              </button>

              <div className="mb-5 text-center">
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={22}
                      className={
                        i < rating ? 'fill-orange-400 text-orange-400' : 'text-gray-200 fill-gray-100'
                      }
                    />
                  ))}
                </div>
                <div className="text-3xl mb-1">{RATING_FEEDBACK[rating]?.emoji}</div>
                {isHighRating ? (
                  <h2 className="text-lg font-bold text-orange-700">高評価ありがとうございます！</h2>
                ) : (
                  <h2 className="text-lg font-bold text-gray-700">評価ありがとうございます。</h2>
                )}
                <p className="text-gray-600 text-sm mt-2">
                  {isHighRating
                    ? 'よろしければ、特によかった点やご感想をお聞かせください。'
                    : 'ご満足いただけず申し訳ありません。至らなかった点や改善点をお聞かせいただけますか？'}
                </p>
              </div>

              {/* ワンタップ タグチップ */}
              <div className="mb-3">
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Sparkles size={12} />
                  ワンタップで挿入
                </div>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const active = comment.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                          active
                            ? isHighRating
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'bg-gray-600 border-gray-600 text-white'
                            : isHighRating
                              ? 'bg-white border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-400'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        {active ? '✓ ' : '+ '}
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-6">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="（例）料理が美味しかった、接客が良かった、〇〇が気になった など"
                  className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-shadow min-h-[120px] text-sm resize-y"
                ></textarea>
                <div className="flex justify-end text-[10px] text-gray-400 mt-1 tabular-nums">
                  {comment.length} / 2000
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-4 rounded-xl font-bold text-white shadow-md flex items-center justify-center gap-2 transition-all ${
                  isSubmitting
                    ? 'bg-orange-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-200 active:scale-[0.98]'
                }`}
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send size={18} />
                    この内容で送信する
                  </>
                )}
              </button>
            </div>
          )}

          {/* STEP 3: 完了＆Googleマップ誘導 */}
          {step === 3 && (
            <div className="text-center animate-in fade-in zoom-in-95 duration-500 relative">
              {isHighRating && <Confetti />}

              {isHighRating ? (
                /* 高評価ルート：Googleマップへ誘導 */
                <div className="space-y-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 text-emerald-500 rounded-full mb-2 animate-bounce-slow">
                    <Heart size={36} className="fill-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">ありがとうございました！</h2>

                  <div className="bg-orange-50 p-5 rounded-xl border border-orange-100 relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <ThumbsUp size={12} />
                      お願い
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed font-medium mb-3 mt-2">
                      もしよろしければ、今入力していただいたご感想を、
                      <span className="text-blue-600 font-bold">Googleマップのクチコミ</span>
                      にも投稿していただけませんか？
                      <br />
                      スタッフの大きな励みになります！
                    </p>

                    {comment && (
                      <div className="mt-4 text-left">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <MessageSquare size={12} />
                          あなたの入力したご感想
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-600 mb-3 break-words">
                          {comment}
                        </div>
                        <button
                          onClick={copyToClipboard}
                          className={`w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all border ${
                            isCopied
                              ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white border-emerald-400 animate-copy-glow'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {isCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                          {isCopied ? 'コピーしました！' : '感想をコピーする'}
                        </button>
                      </div>
                    )}
                  </div>

                  <a
                    href={GOOGLE_MAP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold shadow-md shadow-blue-200 flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
                  >
                    Googleマップでクチコミを書く
                    <ExternalLink size={18} />
                  </a>
                  <p className="text-xs text-gray-400">※ボタンを押すとGoogleマップが開きます</p>
                </div>
              ) : (
                /* 低評価ルート：謝罪と改善の約束のみ（外部へ誘導しない） */
                <div className="space-y-6 py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-500 rounded-full mb-2">
                    <CheckCircle size={32} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">
                    貴重なご意見をいただき、
                    <br />
                    誠にありがとうございます。
                  </h2>
                  <div className="w-12 h-1 bg-gray-200 mx-auto rounded-full"></div>
                  <p className="text-gray-600 text-sm leading-relaxed text-left bg-gray-50 p-4 rounded-xl">
                    今回お寄せいただいたご意見は、店長およびスタッフ全員で真摯に受け止め、より良いお店作りのための改善に役立てさせていただきます。
                    <br />
                    <br />
                    またのご来店を、心よりお待ち申し上げております。
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 5 + Math.random() * 3,
        rotate: Math.random() * 360,
        color: ['#fb923c', '#f472b6', '#60a5fa', '#34d399', '#fbbf24'][i % 5],
        size: 8 + Math.random() * 10,
      })),
    [],
  );
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute -top-4 block rounded-sm animate-confetti-fall"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: p.color,
            transform: `rotate(${p.rotate}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}
